if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        // Strings (fallbacks if theme strings are missing)
        this.strings = {
          addToCart: (window.variantStrings && window.variantStrings.addToCart) || 'Add to cart',
          soldOut:   (window.variantStrings && window.variantStrings.soldOut)   || 'Sold out',
          backorder: (window.variantStrings && window.variantStrings.backorder) || 'Backorder'
        };

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // --- NEW: keep button label/state in sync with current variant, including Backorder ---
        this._refreshButtonFromCurrentVariant = this._refreshButtonFromCurrentVariant.bind(this);
        this._wireVariantChangeObservers();
        // Initial sync on load
        this._refreshButtonFromCurrentVariant();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);

            // After add-to-cart completes, re-sync label in case state changed
            this._refreshButtonFromCurrentVariant();
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = this.strings.addToCart; // use our local strings map
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      // -----------------------------
      // NEW: Backorder-aware updates
      // -----------------------------

      _wireVariantChangeObservers() {
        // (1) Listen to form changes (covers select/radio based pickers)
        this.form.addEventListener('change', this._refreshButtonFromCurrentVariant);

        // (2) Listen for common custom events many themes dispatch
        document.addEventListener('variant:change', this._refreshButtonFromCurrentVariant);
        document.addEventListener('theme:variant:change', this._refreshButtonFromCurrentVariant);
        this.addEventListener('theme:variant:change', this._refreshButtonFromCurrentVariant);

        // (3) Observe the hidden input value attr changes (robust fallback)
        const idInput = this.variantIdInput;
        if (idInput) {
          this._idObserver = new MutationObserver(this._refreshButtonFromCurrentVariant);
          this._idObserver.observe(idInput, { attributes: true, attributeFilter: ['value'] });
        }
      }

      _refreshButtonFromCurrentVariant = () => {
        const idInput = this.variantIdInput;
        if (!idInput) return;
        const id = Number(idInput.value);
        if (!id) {
          this._setSoldOut();
          return;
        }

        // Fetch the up-to-date variant JSON from Shopify (includes availability/inventory fields)
        fetch(`/variants/${id}.js`, { credentials: 'same-origin' })
          .then((r) => r.ok ? r.json() : Promise.reject(new Error('Variant fetch failed')))
          .then((variant) => {
            this._applyVariantToButton(variant);
          })
          .catch(() => {
            // On error, fail safe: enable and show Add to cart so UX isn't blocked
            this._setAddToCart();
          });
      };

      _applyVariantToButton(variant) {
        if (!variant) {
          this._setSoldOut();
          return;
        }

        // 1) If not available => Sold out
        if (!variant.available) {
          this._setSoldOut();
          return;
        }

        // 2) Backorder: managed by Shopify, policy 'continue', and qty <= 0
        const invManaged = (variant.inventory_management || '').toLowerCase() === 'shopify';
        const invPolicy  = (variant.inventory_policy || '').toLowerCase(); // 'continue' | 'deny'
        const invQty     = Number(variant.inventory_quantity || 0);

        if (invManaged && invPolicy === 'continue' && invQty <= 0) {
          this._setBackorder();
          return;
        }

        // 3) Default => Add to cart
        this._setAddToCart();
      }

      _setSoldOut() {
        this.submitButton.setAttribute('disabled', 'disabled');
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButtonText.textContent = this.strings.soldOut;
      }

      _setAddToCart() {
        this.submitButton.removeAttribute('disabled');
        this.submitButton.removeAttribute('aria-disabled');
        this.submitButtonText.textContent = this.strings.addToCart;
      }

      _setBackorder() {
        this.submitButton.removeAttribute('disabled');
        this.submitButton.removeAttribute('aria-disabled');
        this.submitButtonText.textContent = this.strings.backorder;
      }
    }
  );
}
